import React, { useEffect, useMemo, useState } from "react";
import Avatar from "@material-ui/core/Avatar";
import StarsComponent from "../utils/StarsComponent";
import { useLocation, useParams } from "react-router-dom";
import RatingComponent from "./RatingComponent";
import useBDocTranslator from "../hooks/useBDocTranslator";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Snackbar,
  Box,
  CircularProgress,
  Fade,
  Grow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import MuiAlert from "@material-ui/lab/Alert";
import useUserState from "../hooks/useUserState";
import axios from "axios";
import { FeedbackOutlined, StarOutlined } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    padding: theme.spacing(4, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    maxWidth: 500,
    width: "100%",
    borderRadius: 24,
    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
    background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
    border: "1px solid rgba(255,255,255,0.2)",
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 30px 60px rgba(0,0,0,0.15)",
    },
  },
  cardContent: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    background: "linear-gradient(45deg, #076cd8, #4a90e2)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  avatarContainer: {
    position: "relative",
    marginBottom: theme.spacing(3),
    display: "flex",
    justifyContent: "center",
  },
  avatar: {
    width: 140,
    height: 140,
    border: "4px solid #076cd8",
    boxShadow: "0 8px 24px rgba(7, 108, 216, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: "0 12px 32px rgba(7, 108, 216, 0.4)",
    },
  },
  providerName: {
    fontWeight: 500,
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  ratingSection: {
    margin: theme.spacing(3, 0),
    padding: theme.spacing(2),
    background: "rgba(255,255,255,0.6)",
    borderRadius: 16,
    border: "1px solid rgba(7, 108, 216, 0.1)",
  },
  ratingText: {
    padding: theme.spacing(2),
    background: "rgba(7, 108, 216, 0.05)",
    borderRadius: 12,
    margin: theme.spacing(2, 0),
    border: "1px solid rgba(7, 108, 216, 0.1)",
  },
  submitButton: {
    borderRadius: 25,
    padding: theme.spacing(1.5, 4),
    fontSize: "1.1rem",
    fontWeight: 600,
    textTransform: "none",
    background: "linear-gradient(45deg, #076cd8, #4a90e2)",
    color: "white",
    border: "none",
    boxShadow: "0 8px 20px rgba(7, 108, 216, 0.3)",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "linear-gradient(45deg, #065bb0, #3a7bc8)",
      transform: "translateY(-2px)",
      boxShadow: "0 12px 28px rgba(7, 108, 216, 0.4)",
    },
    "&:disabled": {
      background: "#e0e0e0",
      color: "#999",
      boxShadow: "none",
      transform: "none",
    },
  },
  feedbackText: {
    fontStyle: "italic",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    opacity: 0.8,
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 24,
    zIndex: 10,
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export default function Rating() {
  const classes = useStyles();
  const location = useLocation();
  const { rateProvider } = RatingComponent();
  const { t, changeDirectLanguage } = useBDocTranslator();
  const [providerFullname, setProviderFullname] = useState();
  const [profileImage, setProfileImage] = useState();
  const [currentRate, setCurrentRate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { id } = useParams();
  const user = useUserState();
  const userLocalState = useMemo(() => {
    if (!user) return;
    return user[0];
  }, [user]);
  const jwtSession = userLocalState?.jwt;

  const fetchAllProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL_BASE}/appointments/findOne?id=${
          location.state?.appointmentId || id
        }`,
        {
          headers: { Authorization: "Bearer " + jwtSession },
        }
      );

      if (!response.data) return;
      setProviderFullname(
        `${response.data.provider?.firstName} ${response.data.provider?.lastname}`
      );
      if (response.data.provider?.user?.user_images?.length > 0) {
        setProfileImage(
          response.data.provider?.user?.user_images[0].path_resource
        );
      }
    } catch (error) {
      console.log(error);
      setSnackbarMessage(t("Error loading provider information"));
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRateProvider = async () => {
    try {
      setLoading(true);
      await rateProvider(currentRate, location.state?.appointmentId || id);
      setSnackbarMessage(t("Rating submitted successfully"));
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.log(error);
      setSnackbarMessage(
        error.response?.status === 400
          ? error.response.data.message || t("Error submitting rating")
          : t("Error submitting rating")
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const getRatingText = (rate) => {
    switch (rate) {
      case 1:
        return { text: t("Very bad"), color: "error.main" };
      case 2:
        return { text: t("Bad"), color: "error.main" };
      case 3:
        return { text: t("Good"), color: "warning.main" };
      case 4:
        return { text: t("Very good"), color: "success.main" };
      case 5:
        return { text: t("Excellent"), color: "success.main" };
      default:
        return { text: t("Not rated"), color: "text.secondary" };
    }
  };

  useEffect(() => {
    fetchAllProviders();
    changeDirectLanguage("es");
  }, []);

  return (
    <div className={classes.container}>
      <Grow in={true} timeout={800}>
        <Card className={classes.card}>
          {loading && (
            <Fade in={loading}>
              <div className={classes.loadingOverlay}>
                <CircularProgress size={60} style={{ color: "#076cd8" }} />
              </div>
            </Fade>
          )}
          
          <CardContent className={classes.cardContent}>
            <Typography variant="h4" component="h1" className={classes.title}>
              <FeedbackOutlined />
              {t("Rate your experience")}
            </Typography>

            <Fade in={!loading} timeout={1000}>
              <div className={classes.avatarContainer}>
                <Avatar
                  alt={providerFullname}
                  src={profileImage}
                  className={classes.avatar}
                />
              </div>
            </Fade>

            <Fade in={!loading} timeout={1200}>
              <Typography variant="h5" className={classes.providerName}>
                {providerFullname}
              </Typography>
            </Fade>

            <Fade in={!loading} timeout={1400}>
              <Box className={classes.ratingSection}>
                <StarsComponent
                  ratingChanged={(newRating) => {
                    setCurrentRate(newRating);
                  }}
                  editRate={true}
                  rating={currentRate}
                />
              </Box>
            </Fade>

            <Fade in={!loading && currentRate !== null} timeout={1600}>
              <Box className={classes.ratingText}>
                <Typography
                  variant="h6"
                  component="div"
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    gap: "8px"
                  }}
                >
                  <StarOutlined style={{ color: getRatingText(currentRate).color }} />
                  <span>{t("Your rating")}:</span>
                  <Typography
                    variant="h6"
                    component="span"
                    style={{ 
                      color: getRatingText(currentRate).color,
                      fontWeight: 600
                    }}
                  >
                    {getRatingText(currentRate).text}
                  </Typography>
                </Typography>
              </Box>
            </Fade>

            <Fade in={!loading} timeout={1800}>
              <Box mt={3}>
                <Button
                  className={classes.submitButton}
                  onClick={handleRateProvider}
                  disabled={currentRate === null || loading}
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? t("Saving...") : t("Submit Rating")}
                </Button>
              </Box>
            </Fade>

            <Fade in={!loading} timeout={2000}>
              <Typography variant="body2" className={classes.feedbackText}>
                {t("Your feedback helps us improve our service quality")}
              </Typography>
            </Fade>
          </CardContent>
        </Card>
      </Grow>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
